import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { BlogPostSummary } from '@inafana/shared';
import { api } from '@/lib/api';
import { SectionHeading } from '../../components/SectionHeading';
import { formatPostDate } from '../../blog/BlogIndex';
import { prefersReducedMotion } from '../../hooks';

const GAP = 18;

export function LatestNotes(): React.JSX.Element | null {
  const railRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  // Same query key as the blog index, so arriving at /blog from here
  // renders from cache instead of refetching.
  const { data, isError } = useQuery({
    queryKey: ['blog', 'list'],
    queryFn: () => api<{ posts: BlogPostSummary[] }>('/blog'),
  });
  const posts = data?.posts ?? [];

  const sync = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);

  // Re-measure when posts land and whenever the rail is resized —
  // at wide viewports everything fits and both arrows stay disabled.
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync, posts.length]);

  const step = (dir: 1 | -1): void => {
    const el = railRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.note-card');
    const by = card ? card.getBoundingClientRect().width + GAP : el.clientWidth * 0.8;
    el.scrollBy({
      left: dir * by,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  // Nothing to show is not an empty state on the home page — it is a
  // section that should not be there at all.
  if (isError || posts.length === 0) return null;

  return (
    <section id="notes" className="notes">
      <div className="container">
        <div className="notes-head">
          <SectionHeading
            anim="glide"
            title={
              <>
                Notes from <span className="si accent-text">production</span>.
              </>
            }
            lede="What we have learned building agentic AI and the software it runs on — written from things that shipped."
          />
          <div className="notes-controls reveal">
            <Link to="/blog" className="notes-all">
              All notes <ArrowRight size={15} strokeWidth={2} />
            </Link>
            <div className="notes-arrows">
              <button
                type="button"
                className="notes-arrow"
                onClick={() => step(-1)}
                disabled={atStart}
                aria-label="Scroll to previous notes"
              >
                <ArrowLeft size={17} strokeWidth={2} />
              </button>
              <button
                type="button"
                className="notes-arrow"
                onClick={() => step(1)}
                disabled={atEnd}
                aria-label="Scroll to more notes"
              >
                <ArrowRight size={17} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        <div className="notes-rail" ref={railRef} onScroll={sync}>
          <ul className="notes-track">
            {posts.map((post) => (
              <li key={post.id} className="note-card">
                <Link to={`/blog/${post.slug}`}>
                  <span className="note-date">{formatPostDate(post.publishedAt)}</span>
                  <h3 className="note-title">{post.title}</h3>
                  <p className="note-excerpt">{post.excerpt}</p>
                  <span className="note-go">
                    Read note <ArrowRight size={15} strokeWidth={2} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
